import {
  InProgressUnenrollmentDeviceType,
  InProgressUtilityProgramUnenrollment,
} from '@/reducers/inProgressUtilityProgramUnenrollments'
import { Thermostat, Vehicle, VehicleCharger } from '@/types'
import { EnergySystem } from '@/types/energySystem'
import { UtilityProgram } from '@/types/utilityProgram'
import { match } from 'ts-pattern'
import { getArticlesToShowForDevice } from './suggestions/components/utils/getSuggestionsForUnenrollmentReasons'
import { getSettingsForDevice } from './suggestions/components/utils/getSuggestionsForUnenrollmentReasons'

export function unenrollmentVehicleDisplayName(v: Vehicle) {
  return `${v.car.car_model?.year} ${
    v.car.display_name ? `${v.car.display_name} -` : ''
  } ${v.car.car_model?.friendly_name}`
}

export function unenrollmentThermostatDisplayName(t: Thermostat) {
  return `${t.name ?? `Thermostat ${t.id}`}`
}

export function unenrollmentChargerDisplayName(c: VehicleCharger) {
  return `${c.name ?? `Charger ${c.id}`}`
}

export function unenrollmentEnergySystemDisplayName(e: EnergySystem) {
  return `${e.name ?? `Energy System ${e.id}`}`
}

type DeviceSelectionForPresentation = {
  deviceType: InProgressUnenrollmentDeviceType
  articles: {
    url: string
    text: string
  }[]
  settingsWhitelist: string[]
  ids: (string | number)[]
}

/**
 * @returns The selected devices from the in progress unenrollment, with properties
 * for presenting related suggestions added.
 * If the selection does not have any associated suggestions (no settings or articles to show)
 * then the selection is removed from the list.
 */
export function getDeviceSelectionForSuggestionPresentation(
  deviceSelection: InProgressUtilityProgramUnenrollment['deviceSelection'],
  selectedReasons: NonNullable<
    InProgressUtilityProgramUnenrollment['unenrollmentReasons']
  >,
  utilityProgram: UtilityProgram,
): DeviceSelectionForPresentation[] {
  const deviceTypesToPresent = selectedReasons.map(
    (reason) => reason.deviceType,
  )
  const withDeviceTypeAndReasons = Object.entries(deviceSelection).map(
    ([idKey, ids]) => {
      const deviceType: InProgressUnenrollmentDeviceType = match(idKey)
        .with('vehicleIds', () => 'vehicle' as const)
        .with('chargerIds', () => 'charger' as const)
        .with('thermostatIds', () => 'thermostat' as const)
        .with('energySystemIds', () => 'energySystem' as const)
        .run()

      return {
        deviceType: deviceType as InProgressUnenrollmentDeviceType,
        reasons: selectedReasons.filter((r) => r.deviceType === deviceType),
        ids,
      }
    },
  )

  withDeviceTypeAndReasons.push({
    deviceType: 'profile',
    reasons: selectedReasons.filter((r) => r.deviceType === 'profile'),
    ids: [utilityProgram.id],
  })

  const withArticlesAndSettings = withDeviceTypeAndReasons
    .filter((d) => deviceTypesToPresent.includes(d.deviceType))
    .map((d) => ({
      ...d,
      articles: getArticlesToShowForDevice(
        utilityProgram,
        d.deviceType,
        d.reasons,
      ),
      settingsWhitelist: getSettingsForDevice(d.deviceType, d.reasons),
    }))
    // if there are no suggestions for this device type, don't show it
    .filter((d) => d.articles.length > 0 || d.settingsWhitelist.length > 0)

  return withArticlesAndSettings
}
